import type { TCatFormDataInput, TFieldKey } from "@./suf-store";
import { SIGNUP_FLOW_PAGES, SignupFlowSlug } from "../constants";
import type { RequiredFieldData } from "../../../shared/baseStore.types";

import { requiredFieldsBySlug } from "./requiredFieldsBySlug";

/**
 * Slugs whose fields are critical for downstream data correctness.
 *
 * A field is critical if its absence causes incorrect subscription data
 * (wrong portion sizes, tray size, calorie target), fallback API values
 * that misrepresent the cat, or incorrect recipe recommendations.
 *
* Non-critical pages (e.g. eating habits) have safe fallbacks and do not
 * break subscription creation or calorie calculations when missing.
 *
 * When adding a new page or field to the SUF, classify it here.
 * If it is critical, add it to this map. Otherwise, leave it out.
 */
export const CRITICAL_SLUGS = new Set<SignupFlowSlug>([
  SignupFlowSlug.BASIC_INFORMATION,
  SignupFlowSlug.AGE,
  SignupFlowSlug.FITNESS,
  SignupFlowSlug.WEIGHT,
]);

/**
 * Returns critical fields for a given slug, expanding per-cat fields for cat
 * pages (mirroring the same expansion logic used by `getRequiredFieldsForSlug`).
 *
 * Delegates to `requiredFieldsBySlug` for field definitions so there is a
 * single source of truth for which fields each page requires.
 */
export function getCriticalFieldsForSlug(
  slug: SignupFlowSlug,
  cats: Record<string, TCatFormDataInput>,
  context?: Pick<RequiredFieldData, "inABTestGroup">,
): TFieldKey[] {
  if (!CRITICAL_SLUGS.has(slug)) {
    return [];
  }

  const getFields = requiredFieldsBySlug[slug];
  const isUserPage = SIGNUP_FLOW_PAGES[slug]?.isUserPage ?? false;
  if (isUserPage) {
    return getFields({ inABTestGroup: context?.inABTestGroup });
  }

  const fields: TFieldKey[] = [];
  for (const [ catId, cat ] of Object.entries(cats)) {
    if (!cat.softDelete) {
      fields.push(...getFields({ cat, catId, inABTestGroup: context?.inABTestGroup }));
    }
  }
  return fields;
}
